import { useEffect, useState } from "react";
import { api } from "../api";

type Props = {
  /** Quiz slug — used to poll the activity endpoint. */
  slug: string;
  /** Experiment config from the quiz definition. Undefined when the quiz has
   *  no `experiment` set; in that case the bar renders the "not configured"
   *  state so it's still obvious at a glance. */
  experiment?: { intelligemsTestId: string; name: string };
};

// Poll cadence for "still active in the last hour". 60s is frequent enough
// for a setup-time sanity check ("is traffic landing?") without hammering
// the admin API.
const POLL_INTERVAL_MS = 60_000;

export function ExperimentStatusBar({ slug, experiment }: Props) {
  const [count, setCount] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    if (!experiment) {
      setCount(null);
      return;
    }
    let cancelled = false;
    const fetchOnce = () => {
      api
        .experimentStatus(slug)
        .then((res) => {
          if (cancelled) return;
          setCount(res.sessions_with_variant_last_hour);
          setError(false);
        })
        .catch(() => {
          if (cancelled) return;
          setError(true);
        });
    };
    fetchOnce();
    const id = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [slug, experiment]);

  if (!experiment) {
    return (
      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-mars-line bg-mars-charcoal/30 px-4 py-2.5">
        <Dot color="gray" />
        <span className="mono text-[0.7rem] uppercase tracking-[0.14em] text-mars-white/50">
          No experiment configured
        </span>
      </div>
    );
  }

  const active = count !== null && count > 0;
  const dotColor = error ? "red" : active ? "green" : "yellow";

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl border border-mars-line bg-mars-charcoal/30 px-4 py-2.5">
      <div className="flex items-center gap-3">
        <Dot color={dotColor} />
        <span className="mono text-[0.7rem] uppercase tracking-[0.14em] text-mars-white/50">
          Experiment
        </span>
        <span className="text-sm text-mars-white">{experiment.name}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="mono text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/40">
          Configured
        </span>
        <span className="mono text-xs text-mars-white/80">Yes</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="mono text-[0.65rem] uppercase tracking-[0.14em] text-mars-white/40">
          Last hour
        </span>
        <span className="mono text-xs text-mars-white/80">
          {error
            ? "—"
            : typeof count !== "number"
            ? "Loading…"
            : `${count.toLocaleString("en-US")} ${count === 1 ? "session" : "sessions"}`}
        </span>
      </div>
      <span
        className="mono text-[0.6rem] uppercase tracking-[0.14em] text-mars-white/30"
        title={experiment.intelligemsTestId}
      >
        ID: {shortId(experiment.intelligemsTestId)}
      </span>
    </div>
  );
}

function shortId(id: string): string {
  return id.length > 16 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

function Dot({ color }: { color: "gray" | "green" | "yellow" | "red" }) {
  const className =
    color === "green"
      ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]"
      : color === "yellow"
      ? "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.4)]"
      : color === "red"
      ? "bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.4)]"
      : "bg-mars-white/30";
  return <span aria-hidden="true" className={`h-2 w-2 rounded-full ${className}`} />;
}
